import { useFormik } from "formik";
import { FC, useCallback, useEffect } from "react";
import * as yup from "yup";
import { Stack, TextField } from "@mui/material";

import { Dialog } from "components/Dialog";

export interface Props {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (values: FormValues) => void;
}

const NAME_MIN_LENGTH = 3;
const NAME_MAX_LENGTH = 500;

interface FormValues {
  name: string;
  url: string;
}

const initialValues: FormValues = {
  name: "",
  url: ""
};

type FormValuesType = typeof initialValues;

export const CreateCaseLinkDialog: FC<Props> = ({
  isOpen,
  onClose,
  onConfirm
}): JSX.Element => {
  const handleSubmit = useCallback(
    async (
      isValid: boolean,
      values: FormValuesType,
      reset: () => void
    ): Promise<void> => {
      if (!isValid) {
        return;
      }
      onConfirm(values);
      reset();
    },
    [onConfirm]
  );

  const validationSchema = yup.object({
    name: yup
      .string()
      .trim()
      .min(
        NAME_MIN_LENGTH,
        `Name must be at least ${NAME_MIN_LENGTH} characters`
      )
      .max(
        NAME_MAX_LENGTH,
        `Name must be less than ${NAME_MAX_LENGTH} characters`
      ),
    url: yup
      .string()
      .url("URL must be valid")
      .required("URL is required")
  });

  const formik = useFormik<FormValues>({
    initialValues,
    validationSchema,
    validateOnMount: true,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: values => {
      handleSubmit(formik.isValid, values, () => {});
    }
  });

  const closeAndResetDialog = (): void => {
    onClose();

    setTimeout(() => {
      formik.setErrors({});
      formik.resetForm();
    }, 50);
  };

  // Reset form when dialog is opened.
  useEffect(() => {
    if (isOpen) {
      formik.setErrors({});
      formik.resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const shouldDisableConfirm =
    !formik.isValid || (!!Object.keys(formik.touched).length && !formik.dirty);

  return (
    <Dialog
      dialogId="case-create-link-dialog"
      title="Create Link"
      open={isOpen}
      size="small"
      fullWidth={true}
      onClose={closeAndResetDialog}
      data-qa="Case-Create-Link-Dialog"
      confirmDisabled={shouldDisableConfirm}
      confirmText="Save"
      onSubmit={event => {
        event.preventDefault();
        formik.submitForm();
      }}
    >
      <Stack gap={2}>
        <TextField
          variant="outlined"
          fullWidth
          autoFocus
          name="name"
          id="name"
          label="Name"
          data-qa="Case-Link-Text-Name"
          inputProps={{ maxLength: NAME_MAX_LENGTH }}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.name && Boolean(formik.errors.name)}
          helperText={formik.touched.name && formik.errors.name}
          value={formik.values.name}
        />
        <TextField
          variant="outlined"
          fullWidth
          name="url"
          required
          id="url"
          label="URL"
          data-qa="Case-Link-Text-URL"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.url && Boolean(formik.errors.url)}
          helperText={formik.touched.url && formik.errors.url}
          value={formik.values.url}
        />
      </Stack>
    </Dialog>
  );
};
